import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StageConversionData } from "@/services/reportsService";
import { TrendingUp, ArrowRight } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

interface StageConversionChartProps {
  data: StageConversionData[];
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export function StageConversionChart({ data }: StageConversionChartProps) {
  const pieData = data
    .filter(stage => stage.entered > 0)
    .map(stage => ({
      name: stage.from_stage,
      value: stage.rate,
      entered: stage.entered,
      advanced: stage.advanced,
    }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-900">{data.name}</p>
          <p className="text-sm text-slate-600">Conversión: {data.value.toFixed(1)}%</p>
          <p className="text-xs text-slate-500">Entraron: {data.entered}</p>
          <p className="text-xs text-slate-500">Avanzaron: {data.advanced}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <TrendingUp className="h-5 w-5 mr-2" />
          Conversión por Etapa
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pieData.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de Pastel */}
            <div className="flex items-center justify-center">
              <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-2">
                Detalle de Conversiones
              </h4>
              <div className="space-y-2 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
                {data.map((stage, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2.5 bg-slate-50/80 rounded-lg hover:bg-slate-100 transition-all hover:shadow-sm border border-transparent hover:border-slate-200"
                  >
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div className="flex items-center gap-1.5 text-xs min-w-0">
                        <span className="font-medium text-slate-700 truncate">{stage.from_stage}</span>
                        <ArrowRight className="h-3 w-3 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-600 truncate">{stage.to_stage}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <div className="text-sm font-semibold text-slate-900">
                        {stage.rate.toFixed(1)}%
                      </div>
                      <div className="text-xs text-slate-500">
                        {stage.advanced}/{stage.entered}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <TrendingUp className="h-12 w-12 mx-auto mb-2 text-slate-300" />
            <p>No hay datos de conversión disponibles</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
